export function mountNavbar(root) {
	const nav = root.querySelector("[data-solufy-nav]");
	if (!nav) return () => {};

	const toggle = nav.querySelector(".solufy-nav__toggle");
	const menu = nav.querySelector(".solufy-mobile-menu");
	const mobileLinks = menu ? [...menu.querySelectorAll("a:not([data-accordion-ignore])")] : [];
	let open = false;
	let lastScrollY = window.scrollY;

	const setOpen = (next) => {
		open = next;
		nav.classList.toggle("is-menu-open", open);
		if (toggle) toggle.setAttribute("aria-expanded", String(open));
		if (menu) menu.setAttribute("aria-hidden", String(!open));
		document.body.classList.toggle("solufy-menu-open", open);
		if (open) nav.classList.remove("is-hidden");
	};

	const onToggle = () => setOpen(!open);

	// Desktop Megamenu handling with strict mutual exclusivity
	const megamenuItems = [...nav.querySelectorAll("[data-megamenu]")];
	const scrim = root.querySelector("[data-megamenu-scrim]");
	let activeMegamenu = null;
	let closeTimeout = null;

	const closeAllMegamenus = () => {
		if (closeTimeout) {
			clearTimeout(closeTimeout);
			closeTimeout = null;
		}
		megamenuItems.forEach((item) => {
			item.classList.remove("is-open");
			const trigger = item.querySelector(".solufy-nav__trigger");
			if (trigger) trigger.setAttribute("aria-expanded", "false");
		});
		activeMegamenu = null;
		document.body.classList.remove("solufy-megamenu-active");
	};

	const openMegamenu = (item) => {
		if (closeTimeout) {
			clearTimeout(closeTimeout);
			closeTimeout = null;
		}
		if (activeMegamenu === item) return;
		closeAllMegamenus();
		item.classList.add("is-open");
		const trigger = item.querySelector(".solufy-nav__trigger");
		if (trigger) trigger.setAttribute("aria-expanded", "true");
		activeMegamenu = item;
		document.body.classList.add("solufy-megamenu-active");
	};

	const scheduleClose = () => {
		if (closeTimeout) clearTimeout(closeTimeout);
		closeTimeout = setTimeout(() => {
			closeAllMegamenus();
		}, 180);
	};

	megamenuItems.forEach((item) => {
		const trigger = item.querySelector(".solufy-nav__trigger");
		const menu = item.querySelector(".solufy-megamenu");

		if (trigger) {
			trigger.addEventListener("click", (e) => {
				e.stopPropagation();
				if (item.classList.contains("is-open")) {
					closeAllMegamenus();
				} else {
					openMegamenu(item);
				}
			});
		}

		item.addEventListener("mouseenter", () => {
			if (window.innerWidth >= 1101) {
				openMegamenu(item);
			}
		});

		item.addEventListener("mouseleave", () => {
			if (window.innerWidth >= 1101) {
				scheduleClose();
			}
		});

		if (menu) {
			menu.addEventListener("mouseenter", () => {
				if (closeTimeout) {
					clearTimeout(closeTimeout);
					closeTimeout = null;
				}
			});
			menu.addEventListener("mouseleave", () => {
				if (window.innerWidth >= 1101) {
					scheduleClose();
				}
			});
		}
	});

	if (scrim) {
		scrim.addEventListener("click", () => {
			closeAllMegamenus();
		});
	}

	// Close all menus immediately on ANY navigation link click
	const allNavLinks = [...nav.querySelectorAll("a")];
	allNavLinks.forEach((link) => {
		link.addEventListener("click", () => {
			closeAllMegamenus();
			setOpen(false);
			if (document.activeElement && document.activeElement.blur) {
				document.activeElement.blur();
			}
		});
	});

	// Mobile Accordion handling
	const mobileAccordions = menu ? [...menu.querySelectorAll("[data-mobile-accordion]")] : [];
	mobileAccordions.forEach((acc) => {
		const btn = acc.querySelector(".solufy-mobile-accordion__btn");
		if (btn) {
			btn.addEventListener("click", (e) => {
				e.preventDefault();
				e.stopPropagation();
				const isOpen = acc.classList.contains("is-open");
				// Close other open accordions
				mobileAccordions.forEach((other) => {
					if (other !== acc) {
						other.classList.remove("is-open");
						const otherBtn = other.querySelector(".solufy-mobile-accordion__btn");
						if (otherBtn) otherBtn.setAttribute("aria-expanded", "false");
					}
				});
				acc.classList.toggle("is-open", !isOpen);
				btn.setAttribute("aria-expanded", String(!isOpen));
			});
		}
	});

	const onKeydown = (event) => {
		if (event.key === "Escape") {
			setOpen(false);
			closeAllMegamenus();
		}
	};

	const onClickOutside = (event) => {
		if (!event.target.closest("[data-megamenu]")) {
			closeAllMegamenus();
		}
	};

	const onScroll = () => {
		const currentScrollY = Math.max(0, window.scrollY);
		nav.classList.toggle("is-scrolled", currentScrollY > 16);
		if (open || currentScrollY < 40) {
			nav.classList.remove("is-hidden");
			document.body.classList.remove("is-main-nav-hidden");
			lastScrollY = currentScrollY;
		} else if (currentScrollY > lastScrollY + 5) {
			nav.classList.add("is-hidden");
			document.body.classList.add("is-main-nav-hidden");
			closeAllMegamenus();
			lastScrollY = currentScrollY;
		} else if (currentScrollY < lastScrollY - 5) {
			nav.classList.remove("is-hidden");
			document.body.classList.remove("is-main-nav-hidden");
			lastScrollY = currentScrollY;
		}
	};

	const onResize = () => {
		const desktop = window.matchMedia("(min-width: 1101px)").matches;
		if (desktop && open) setOpen(false);
		if (!desktop) closeAllMegamenus();
	};

	// Reset state when page is restored from bfcache or unloaded
	const onPageTransition = () => {
		closeAllMegamenus();
		setOpen(false);
		if (document.activeElement && document.activeElement.blur) {
			document.activeElement.blur();
		}
	};

	if (toggle) toggle.addEventListener("click", onToggle);
	document.addEventListener("keydown", onKeydown);
	document.addEventListener("click", onClickOutside);
	window.addEventListener("scroll", onScroll, { passive: true });
	window.addEventListener("resize", onResize, { passive: true });
	window.addEventListener("pageshow", onPageTransition);
	window.addEventListener("pagehide", onPageTransition);
	window.addEventListener("beforeunload", onPageTransition);
	onScroll();

	return () => {
		setOpen(false);
		closeAllMegamenus();
		if (toggle) toggle.removeEventListener("click", onToggle);
		document.removeEventListener("keydown", onKeydown);
		document.removeEventListener("click", onClickOutside);
		window.removeEventListener("scroll", onScroll);
		window.removeEventListener("resize", onResize);
		window.removeEventListener("pageshow", onPageTransition);
		window.removeEventListener("pagehide", onPageTransition);
		window.removeEventListener("beforeunload", onPageTransition);
	};
}
